import { useState, useEffect } from 'react';
import useSiteSettings from '@/lib/useSiteSettings';
import PictureImg from '@/components/PictureImg';

/**
 * BrandMark — renders either the user-uploaded logo (if present) or the
 * typographic Maison Touré wordmark. Stays inside the museum-grade visual
 * language: never resizes containers, just swaps the inner content.
 *
 * Props
 *  - variant: 'light' (for dark backgrounds) | 'dark' (for light backgrounds)
 *  - size: pixel height for the logo image (defaults differ per surface)
 *  - inkColor / subColor: text fallback colors when no logo uploaded
 *  - showSubline: render the small tracked-caps subline beneath the wordmark
 */
export default function BrandMark({
    variant = 'dark',
    size = 36,
    inkColor = 'var(--ink)',
    subColor = 'var(--ink-2)',
    showSubline = true,
    wordmarkSize = 26,
    sublineSize = 9,
}) {
    const settings = useSiteSettings();
    const brand = settings?.brand || {};
    const logoUrl = variant === 'light'
        ? (brand.logo_primary_light || '/api/uploads/968d06f07d394ad1a42e892769941293.svg')
        : (brand.logo_primary_dark || '/api/uploads/45d5b0db1ed940b4989b6bcac5f60d3a.svg');

    // If the configured logo fails to load (broken URL, blocked by CORS, etc.)
    // we fall back to the typographic wordmark so the nav is never empty.
    const [imgFailed, setImgFailed] = useState(false);
    useEffect(() => { setImgFailed(false); }, [logoUrl]);

    if (logoUrl && !imgFailed) {
        return (
            <PictureImg
                src={logoUrl}
                alt="Maison Touré"
                data-testid={`brand-logo-${variant}`}
                onError={() => setImgFailed(true)}
                style={{
                    display: 'block',
                    height: size,
                    width: 'auto',
                    maxWidth: 280,
                    objectFit: 'contain',
                    /* Prevent flex-cross-axis stretch collapsing the img to 0×0
                       when the parent is a flex container (the Nav <Link> is
                       `flex flex-col`).  */
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                    transition: 'opacity .6s, height .4s',
                }}
            />
        );
    }

    return (
        <span className="flex flex-col leading-none" data-testid={`brand-wordmark-${variant}`}>
            <span
                className="serif"
                style={{
                    fontSize: wordmarkSize,
                    letterSpacing: '-0.015em',
                    fontWeight: 400,
                    color: inkColor,
                    transition: 'color .6s',
                }}
            >
                Maison Touré
            </span>
            {showSubline && (
                <span
                    className="overline mt-[6px]"
                    style={{
                        fontSize: sublineSize,
                        letterSpacing: '0.34em',
                        color: subColor,
                        transition: 'color .6s',
                    }}
                >
                    Fine Contemporary African Art
                </span>
            )}
        </span>
    );
}
